/**
 * Figma Node IDs:
 * Design Frame: 2473:21910
 * Instance IDs: 334:41839, 812:11977, 833:2180, 334:41842, 812:11980, 833:2184, 2016:12636
 * Composite: true
 * Last Updated: 2024-12-23
 * Generated by: Claude Code figma-component command
 */
import React, { useState, useEffect, FC } from "react";
import { useIntl, defineMessages } from "react-intl";
import { Root, Trigger, Content } from "@radix-ui/react-collapsible";
import { ChevronDown, Lock } from "@labxchange/icons";
import { srOnly } from "@labxchange/design_tokens";
import {
  collapsibleHeaderClass,
  collapsibleLabelClass,
  collapsibleContentClass,
  chevronClass,
  lockIconClass,
  sleekCollapsibleContainerClass,
  boxCollapsibleContainerClass,
  boxCollapsibleTriggerClass,
  sleekCollapsibleTriggerClass,
  sleekCollapsibleLabelClass,
  sleekCollapsibleContentClass,
} from "./Collapsible.css";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

export const messages = defineMessages({
  locked: {
    id: "LxCollapsible.locked",
    defaultMessage: "Locked",
    description:
      "Announced before the section label when a collapsible cannot be expanded",
  },
});

interface CollapsibleProps {
  label: string;
  content: string | React.ReactNode;
  open: boolean;
  variant?: "sleek" | "box";
  locked?: boolean;
}

const containsMathJax = (text: string | undefined): boolean => {
  if (!text || typeof text !== "string") return false;
  return /\$\$.*?\$\$|\$.*?\$/g.test(text);
};

export const Collapsible: FC<CollapsibleProps> = ({
  variant = "box",
  label,
  content,
  open,
  locked = false,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(open);

  const containerClass = [
    boxCollapsibleContainerClass,
    variant === "sleek" && sleekCollapsibleContainerClass,
  ]
    .filter(Boolean)
    .join(" ");
  const triggerClass = [
    boxCollapsibleTriggerClass,
    variant === "sleek" && sleekCollapsibleTriggerClass,
  ]
    .filter(Boolean)
    .join(" ");

  const labelClass = [
    variant === "sleek" && sleekCollapsibleLabelClass,
    variant === "box" && collapsibleLabelClass,
  ]
    .filter(Boolean)
    .join(" ");

  const contentClass = [
    collapsibleContentClass,
    variant === "sleek" && sleekCollapsibleContentClass,
  ]
    .filter(Boolean)
    .join(" ");

  const handleOpenChange = (newOpen: boolean) => {
    if (!locked) {
      setIsOpen(newOpen);
    }
  };

  useEffect(() => {
    if (locked) {
      setIsOpen(false);
    }
  }, [locked]);

  return (
    <Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={containerClass}
      data-locked={locked}
    >
      <div className={collapsibleHeaderClass}>
        <Trigger
          className={triggerClass}
          data-locked={locked}
          disabled={locked}
        >
          {/*
            `disabled` only conveys "unavailable" (screen readers say "dimmed"),
            not *why*. There is no ARIA state for "locked", so the state is
            supplied as visually-hidden text at the start of the trigger's
            accessible name, ahead of the label.
          */}
          {locked && (
            <span className={srOnly}>
              {intl.formatMessage(messages.locked)}
            </span>
          )}
          <span className={labelClass}>
            <RichText value={label} />
          </span>
          {locked ? (
            <Lock className={lockIconClass} size={18} aria-hidden="true" />
          ) : (
            <ChevronDown
              className={chevronClass}
              size={18}
              aria-hidden="true"
            />
          )}
        </Trigger>
      </div>

      <div>
        <Content className={contentClass}>
          {isOpen && (
            <>
              {typeof content === "string" ? (
                <RichText value={content} equation={containsMathJax(content)} />
              ) : (
                content
              )}
            </>
          )}
        </Content>
      </div>
    </Root>
  );
};
